import { zodResolver } from "@hookform/resolvers/zod";
import { Box, CircularProgress, makeStyles, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import React from "react";
import { useForm, Controller } from "react-hook-form";
import { useHistory, useLocation } from "react-router";
import { useRecoilValueLoadable, useSetRecoilState } from "recoil";

import { BuildingRoom } from "@/api/generated";
import { PROPERTY_TYPE_VALUES } from "@/api/mappings/property";
import { CustomButton } from "@/components/Button";
import { PropertyEditErrorSummary } from "@/components/property_edit/PropertyEditErrorSummary";
import { CustomForm } from "@/components/property_edit/PropertyEditInputs";
import { CustomFormLabel } from "@/components/property_edit/PropertyEditInputs";
import { DatePickerComponent } from "@/components/property_edit/PropertyEditInputs";
import { PropertyEditLayout as BaseTemplate } from "@/components/property_edit/PropertyEditLayout";
import { Toast } from "@/components/Toast";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import theme from "@/theme";
import { parseFormattedNumber } from "@/utilities/format";
import { roomInfoSchema, RoomInfoFormData } from "@/validations/management/edit";
import { PropertyManagementDetailSnackbarState, useRequestInitialLoading } from "@/view_models/atoms";
import { SelectedProperty } from "@/view_models/propertySelectors";

type Props = {
  buildingRoom?: BuildingRoom;
  onSubmit: (data: RoomInfoFormData) => void;
  onDelete?: () => void;
  onCancel: () => void;
  onSave?: () => void;
  onCreate?: () => void;
};

const useStyles = makeStyles(() => ({
  container: {
    background: theme.palette.background.paper,
    borderRadius: 12,
    maxWidth: "100%",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
  },
  formItem: {
    marginBottom: theme.spacing(2),
  },
  label: {
    fontWeight: 700,
    fontSize: "0.9rem",
    marginBottom: 4,
    display: "inline-block",
  },
  helperText: {
    fontSize: "0.75rem",
    color: theme.palette.grey[700],
    marginTop: 4,
  },
  buttonGroup: {
    marginTop: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    alignItems: "center",
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
  cancelButton: {
    color: theme.palette.text.disabled,
  },
  background: {
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    padding: 16,
    marginBottom: 8,
  },
}));

export const ManagementEditRoomPage: React.FC<Props> = ({ onCreate }) => {
  const history = useHistory();
  const location = useLocation();
  const classes = useStyles();
  const {
    control,
    formState: { errors },
    getValues,
    trigger,
    handleSubmit,
  } = useForm<RoomInfoFormData>({
    resolver: zodResolver(roomInfoSchema),
  });
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get("mode") || "edit";

  const selectedRoomId = queryParams.get("selectedRoomId");

  const propertyLoadable = useRecoilValueLoadable(SelectedProperty);
  const selected_property = propertyLoadable.state === "hasValue" ? propertyLoadable.contents : null;
  const selectedRoomIdNumber = selectedRoomId ? Number(selectedRoomId) : NaN;
  const selectedRoom =
    selected_property && Number.isFinite(selectedRoomIdNumber) && selected_property.building_room?.length
      ? selected_property.building_room.find((room) => room.id === selectedRoomIdNumber)
      : undefined;

  // 物件詳細でのスナックバー表示
  const setManagementDetailSnackBarState = useSetRecoilState(PropertyManagementDetailSnackbarState);

  const requestInitialLoading = useRequestInitialLoading();

  const [serverError, setServerError] = React.useState("");

  const pageBack = React.useCallback(() => {
    const propertyType = selected_property?.property?.property_type;
    if (propertyType === PROPERTY_TYPE_VALUES.consideration) {
      history.push("/management/consideration/detail");
      return;
    }
    if (propertyType === PROPERTY_TYPE_VALUES.owned) {
      history.push("/management/own/detail");
      return;
    }
    history.goBack();
  }, [history, selected_property?.property?.property_type]);

  const onCancel = () => {
    pageBack();
  };

  const selectedPropertyId = selected_property?.id;
  const selectedRoomIdToEdit = selectedRoom?.id;

  const onDelete = React.useCallback(async () => {
    if (!selectedPropertyId || !selectedRoomIdToEdit) {
      setManagementDetailSnackBarState({
        message: "部屋情報が見つかりませんでした",
        status: "error",
        isOpen: true,
      });
      pageBack();
      return;
    }
    const fp = PropallyApiFactory();
    try {
      const response = await fp.v1PropertiesPropertyIdBuildingRoomsBuildingRoomIdDelete(
        selectedPropertyId,
        selectedRoomIdToEdit,
        {
          withCredentials: true,
        }
      );
      if (response.status === 200) {
        pageBack();
        requestInitialLoading();
      }
    } catch (e) {
      console.error(e);
      setManagementDetailSnackBarState({
        message: "物件情報を変更できませんでした",
        status: "error",
        isOpen: true,
      });
      pageBack();
    }
  }, [pageBack, requestInitialLoading, selectedPropertyId, selectedRoomIdToEdit, setManagementDetailSnackBarState]);

  const onSubmit = async (onSuccess: () => void) => {
    if (!selectedPropertyId) {
      setManagementDetailSnackBarState({
        message: "物件情報が見つかりませんでした",
        status: "error",
        isOpen: true,
      });
      pageBack();
      return;
    }
    const fp = PropallyApiFactory();
    const values = getValues();
    // Trigger validation
    const isValid = await trigger();
    if (!isValid) {
      return;
    }

    const { ...restValues } = values;
    const sendData: BuildingRoom = {
      ...restValues,
      room_number: values.room_number,
      starts_at: `${dayjs(values.starts_at).format("YYYY-MM")}-${values.starts_at_day}`,
      ends_at: `${dayjs(values.ends_at).format("YYYY-MM")}-${values.ends_at_day}`,
      monthly_fee: parseFormattedNumber(values.monthly_fee),
      other_monthly_income: parseFormattedNumber(values.other_monthly_income),
    };

    try {
      // idがある場合は更新、ない場合は新規作成
      // 更新
      if (selectedRoomIdToEdit) {
        const result = await fp.v1PropertiesPropertyIdBuildingRoomsBuildingRoomIdPatch(
          selectedPropertyId,
          selectedRoomIdToEdit,
          sendData,
          {
            withCredentials: true,
          }
        );
        if (result.status === 200) {
          requestInitialLoading();
        }
      } else {
        // 新規作成
        const result = await fp.v1PropertiesPropertyIdBuildingRoomsPost(selectedPropertyId, sendData, {
          withCredentials: true,
        });
        if (result.status === 200) {
          requestInitialLoading();
        }
      }
      // APIの処理が成功した時に実行する。
      if (onSuccess) {
        onSuccess();
      }
    } catch (e) {
      console.error(e);
      setManagementDetailSnackBarState({
        message: "物件情報を変更できませんでした",
        status: "error",
        isOpen: true,
      });
    } finally {
      pageBack();
    }
  };

  const submitHandler = (data: RoomInfoFormData) => {
    try {
      const formattedData: RoomInfoFormData = {
        ...data,
        monthly_fee: parseFormattedNumber(data.monthly_fee),
        other_monthly_income: parseFormattedNumber(data.other_monthly_income),
      };
      console.log("formattedData ", formattedData);
      // onSubmit(formattedData);
      onSubmit(onCreate);
    } catch (e) {
      console.error(e);
      setServerError("送信中にエラーが発生しました。");
    }
  };

  const handleCloseToast = () => setServerError("");

  let headerTitle: string;
  if (mode == "edit") {
    headerTitle = "部屋情報編集";
  } else {
    headerTitle = "部屋情報登録";
  }

  if (propertyLoadable.state === "loading")
    return (
      <BaseTemplate headerTitle={headerTitle} onBack={pageBack}>
        <Box role="status" aria-label="部屋情報を読み込み中" display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} />
        </Box>
      </BaseTemplate>
    );
  if (propertyLoadable.state === "hasError" || !selected_property || (mode === "edit" && !selectedRoom))
    return (
      <BaseTemplate headerTitle={headerTitle} onBack={pageBack}>
        <Typography role="alert">部屋情報を読み込めませんでした。物件の詳細からもう一度開いてください。</Typography>
        <CustomButton type="button" tone="text" onClick={pageBack}>
          物件の詳細へ戻る
        </CustomButton>
      </BaseTemplate>
    );

  return (
    <BaseTemplate
      headerTitle={headerTitle}
      heading={mode === "edit" && selectedRoom ? `${selectedRoom.room_number}号室の確認・編集` : undefined}
      onBack={onCancel}
      isBackGroundWhite={true}
    >
      <Box className={classes.container} component="form" onSubmit={handleSubmit(submitHandler)}>
        <Box data-edit-form-panel className={classes.background}>
          <Typography component="h3" data-edit-section>
            部屋
          </Typography>
          {/* 部屋番号 */}
          <Controller
            name="room_number"
            control={control}
            defaultValue={selectedRoom?.room_number || ""}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="部屋番号"
                required
                error={!!errors.room_number}
                errorText={errors.room_number?.message}
                placeHolder="102"
                unitSuffix="号室"
              />
            )}
          />

          <Typography component="h3" data-edit-section>
            家賃と契約
          </Typography>
          {/* 月間家賃収入 */}
          <Controller
            name="monthly_fee"
            control={control}
            defaultValue={selectedRoom?.monthly_fee || ""}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="家賃"
                required
                error={!!errors.monthly_fee}
                errorText={errors.monthly_fee?.message}
                placeHolder="100,000,000"
                unitSuffix="円"
                formatNumeric
              />
            )}
          />

          {/* その他収入 */}
          <Controller
            name="other_monthly_income"
            control={control}
            defaultValue={selectedRoom?.other_monthly_income || ""}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="その他の収入（駐車場など）"
                required
                error={!!errors.other_monthly_income}
                errorText={errors.other_monthly_income?.message}
                placeHolder="100,000,000"
                unitSuffix="円"
                formatNumeric
              />
            )}
          />

          {/* 賃貸契約の開始月 */}

          <Box mb={1}>
            <CustomFormLabel label="賃貸借契約の開始月" required info="契約に関する年月日を選択してください。" />
          </Box>

          <Controller
            name="starts_at"
            control={control}
            defaultValue={selectedRoom?.starts_at ? dayjs(selectedRoom.starts_at).toDate() : dayjs().toDate()}
            render={({ ...field }) => <DatePickerComponent labelLight="年月" {...field} />}
          />

          <Controller
            name="starts_at_day"
            control={control}
            defaultValue={selectedRoom?.starts_at ? dayjs(selectedRoom.starts_at).format("D") : "1"}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="日にち"
                helperText="不明の場合は値を「1日」のままお進みください。"
                error={!!errors.starts_at_day}
                errorText={errors.starts_at_day?.message}
                onChange={field.onChange}
                placeHolder="1"
                unitSuffix="日"
                isShort
              />
            )}
          />

          {/* 賃貸契約の終了月 */}

          <Box mb={1}>
            <CustomFormLabel label="賃貸借契約の終了月" required info="契約に関する年月日を選択してください。" />
          </Box>

          <Controller
            name="ends_at"
            control={control}
            defaultValue={selectedRoom?.ends_at ? dayjs(selectedRoom.ends_at).toDate() : dayjs().toDate()}
            render={({ ...field }) => (
              <DatePickerComponent
                {...field}
                labelLight="年月"
                error={!!errors.ends_at}
                errorText={errors.ends_at?.message}
              />
            )}
          />

          <Controller
            name="ends_at_day"
            control={control}
            defaultValue={selectedRoom?.ends_at ? dayjs(selectedRoom.ends_at).format("D") : "1"}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="日にち"
                helperText="不明の場合は値を「1日」のままお進みください。"
                error={!!errors.ends_at_day}
                errorText={errors.ends_at_day?.message}
                onChange={field.onChange}
                placeHolder="1"
                unitSuffix="日"
                isShort
              />
            )}
          />
        </Box>

        <PropertyEditErrorSummary errors={errors} />
        {/* ボタン類 */}
        <Box className={classes.buttonGroup}>
          {mode === "edit" ? (
            <>
              <CustomButton type="submit" buttonSize="L" customVariant="filled">
                保存する
              </CustomButton>
              <CustomButton type="button" tone="text" className={classes.deleteButton} onClick={onDelete}>
                この部屋を削除する
              </CustomButton>
            </>
          ) : (
            <>
              <CustomButton type="submit" buttonSize="L" customVariant="filled">
                この部屋を追加する
              </CustomButton>
              <CustomButton type="button" tone="text" className={classes.cancelButton} onClick={onCancel}>
                キャンセル
              </CustomButton>
            </>
          )}
        </Box>

        <Toast open={!!serverError} variant="error" message={serverError} onClose={handleCloseToast} />
      </Box>
    </BaseTemplate>
  );
};
